import { Controller } from "@hotwired/stimulus"

// Arrow + edge-fade controls for the home category rail, mirroring the target
// design: scroll ~4 cards per click (capped at 720px), loop back to the start
// once the end is reached (the right arrow swaps to a loop icon there), and
// fade the leading/trailing edge to hint there's more to scroll.
// `scrollBy` keeps smooth scroll scoped — never set scroll-behavior on
// html/body (it breaks Turbo).
export default class extends Controller<HTMLElement> {
  static targets = ["rail", "prev", "leftFade", "rightFade", "nextIcon", "loopIcon"]

  declare readonly railTarget: HTMLElement
  declare readonly prevTarget: HTMLElement
  declare readonly leftFadeTarget: HTMLElement
  declare readonly rightFadeTarget: HTMLElement
  declare readonly nextIconTarget: HTMLElement
  declare readonly loopIconTarget: HTMLElement

  connect() {
    requestAnimationFrame(() => this.update())
  }

  scroll(event: Event) {
    const direction = Number((event.currentTarget as HTMLElement).dataset.direction) || 1

    if (direction > 0 && this.canLoop) {
      this.railTarget.scrollTo({ left: 0, behavior: "smooth" })
    } else {
      const distance = Math.min(this.railTarget.clientWidth * 0.82, 720)
      this.railTarget.scrollBy({ left: direction * distance, behavior: "smooth" })
    }
  }

  update() {
    this.leftFadeTarget.classList.toggle("opacity-0", this.atStart)
    this.rightFadeTarget.classList.toggle("opacity-0", this.atEnd)
    this.prevTarget.classList.toggle("opacity-40", this.atStart)
    this.prevTarget.toggleAttribute("disabled", this.atStart)
    this.nextIconTarget.classList.toggle("hidden", this.canLoop)
    this.loopIconTarget.classList.toggle("hidden", !this.canLoop)
  }

  get maxScroll() {
    return Math.max(0, this.railTarget.scrollWidth - this.railTarget.clientWidth)
  }

  get atStart() {
    return this.railTarget.scrollLeft <= 4
  }

  get atEnd() {
    return this.maxScroll <= 4 || this.railTarget.scrollLeft >= this.maxScroll - 4
  }

  // Only offer the "loop back to start" affordance when the rail actually overflows.
  get canLoop() {
    return this.maxScroll > 4 && this.atEnd
  }
}
